import {
  Component,
  ElementRef,
  EventEmitter,
  input,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {MatIconModule} from "@angular/material/icon";
import {MatButtonModule} from "@angular/material/button";
import {NgForOf, NgIf} from "@angular/common";
import {Hymn} from "../../hymn/hymn";
import {ConfettiService} from "../../service/confetti.service";
import {translation} from "../../translation";

//define extension of Hymn with status field
export interface HymnWithStatus extends Hymn {
  status: string;
}

@Component({
  selector: 'app-game',
  standalone: true,
  imports: [MatIconModule, MatButtonModule, NgForOf, NgIf],
  templateUrl: './game.component.html',
  styleUrl: './game.component.scss'
})
export class GameComponent implements OnInit, OnDestroy {
  options: HymnWithStatus[] = [];
  winningOption?: Hymn;
  countdown: number = 0;

  timeRoundStart: number = 0;
  timeRoundEnd: number = 0;

  @Output() optionSelected = new EventEmitter<Hymn>();
  @Output() begin = new EventEmitter<void>();
  @Output() restart = new EventEmitter<void>();
  @Output() exit = new EventEmitter<void>();
  @Output() exitImmediate = new EventEmitter<void>();
  @Output() info = new EventEmitter<void>();

  selectionMade: boolean = false;
  private startTime = 0;
  darkOverlay = false;
  @Input() player: number = 1;
  @Input() message: string = "";
  @Input() subMessage: string | null = null;

  @Input() enableHints: boolean = false;
  showHints = false;
  // showHintButton1: boolean = false;
  // showHintButton2: boolean = false;
  // showHintButton3: boolean = false;

  lyricsHintsLeft: number = 3;
  fiftyFiftyHintsLeft: number = 3;

  darkBackground: boolean = true;

  disableOptions = true;

  initalMode = true;
  afterGame = false;

  hintUsed5050 = false;
  hintUsedLyrics = false;

  timeouts: any = [];

  @Input() playButtonActive = false;
  @Output() midiplay = new EventEmitter<void>();
  @Output() midistop = new EventEmitter<void>();

  constructor(protected confettiService: ConfettiService,
              private ngZone: NgZone) {
  }

  ngOnInit() {
    this.initialize();
  }

  ngOnDestroy() {
    this.confettiService.stop();
    this.clearTimeouts();
  }

  async initialize() {
    this.initalMode = true;
    this.afterGame = false;
    this.darkOverlay = false;
    this.countdown = 0;
  }

  async nextRound(options: HymnWithStatus[], winningOption: Hymn, startTime: number) {
    this.initalMode = false;
    this.afterGame = false;
    this.disableOptions = true;
    this.countdown = 3;
    this.selectionMade = false;
    this.darkOverlay = false;
    this.subMessage = null;
    this.resetHints();

    await this.performCountdown();
    this.darkBackground = false;
    // Remove the next four hymns from the list
    //this.options = this.hymns.slice(this.round*4, this.round*4 + 4);
    this.options = options;
    this.winningOption = winningOption;
    this.disableOptions = false;

    this.timeRoundStart = new Date().getTime();
    if(this.enableHints) {
      this.showHints = true; // Make the parent div visible
    }
  }

  async performCountdown() {
    for (let i = this.countdown; i > 0; i--) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      this.countdown--;
    }
  }

  clearTimeouts() {
    this.timeouts.forEach((t: any) => {
      if(t) {
        clearTimeout(t);
      }
    });
    this.timeouts = [];
  }

  resetHints() {
    this.showHints = false;
    // this.showHintButton1 = false;
    // this.showHintButton2 = false;
    // this.showHintButton3 = false;
    this.hintUsed5050 = false;
    this.hintUsedLyrics = false;
  }

  chooseHymnOption(chosenOption: HymnWithStatus) {
    this.timeRoundEnd = new Date().getTime();

    if(!this.disableOptions && chosenOption.status === 'none') {

      this.selectionMade = true;
      this.darkOverlay = true;
      this.clearTimeouts();

      console.log('Chosen option:', chosenOption);

      // Color the options based on the user's choice
      this.options.forEach(o => {
        if (o.status === 'invisible') {
          return;
        }
        if (o.hymnNumber === chosenOption.hymnNumber) {
          if (o.hymnNumber === this.winningOption?.hymnNumber) {
            o.status = 'correct';
          } else {
            o.status = 'incorrect';
          }
        } else if (o.hymnNumber === this.winningOption?.hymnNumber) {
          o.status = 'winning';
        } else {
          o.status = 'disabled';
        }
      });

      this.optionSelected.emit(chosenOption);
    }
  }

  hidePlayer() {
    this.darkOverlay = true;
  }
  showPlayer() {
    this.darkOverlay = false;
  }

  setMessage(message: string) {
    this.message = message;
  }

  getPlayerNumber() {
    return this.player;
  }

  setDarkBackground(darkBackground: boolean) {
    this.darkBackground = darkBackground;
  }

  setDisableOptions(disableOptions: boolean) {
    this.disableOptions = disableOptions;
  }

  beginClicked() {
    this.begin.emit();
  }

  win(message: string, oppositeLayout: boolean, fullscreenConfetti = false, fireworksDuration = 10) {
    this.afterGame = true;
    this.darkOverlay = false;
    this.darkBackground = false;
    this.message = message;
    this.subMessage = null;
    this.options = [];

    if(!fullscreenConfetti) {
      if (this.player == 1) {
        oppositeLayout ?
          this.confettiService.fireworksPlayerOneOppositeLayout()
          : this.confettiService.fireworksPlayerOneSideBySideLayout();
      } else {
        oppositeLayout ?
          this.confettiService.fireworksPlayerTwoOppositeLayout()
          : this.confettiService.fireworksPlayerTwoSideBySideLayout();
      }
    } else {
      this.confettiService.fireworksFullScreen(fireworksDuration);
    }
  }

  lose(message: string) {
    this.afterGame = true;
    this.darkOverlay = false;
    this.darkBackground = true;
    this.message = message;
    this.subMessage = null;
    this.options = [];
  }

  draw(message: string) {
    this.afterGame = true;
    this.darkOverlay = false;
    this.darkBackground = false;
    this.message = message;
    this.subMessage = null;
    this.options = [];

    this.player == 1? this.confettiService.fireworksPlayerOneOppositeLayout(2) : this.confettiService.fireworksPlayerTwoOppositeLayout(2);
  }

  onExit() {
    this.confettiService.stop();
    this.exit.emit();
  }

  onExitImmediate() {
    this.confettiService.stop();
    this.exitImmediate.emit();
  }

  onRestart() {
    this.confettiService.stop();
    this.restart.emit();
  }

  onInfo() {
    this.info.emit();
  }

  onHint5050() {
    if(!this.hintUsed5050) {
      this.hintUsed5050 = true;
      this.fiftyFiftyHintsLeft--;

      // Disable two incorrect options
      let count = 0;
      let index = 0;
      const shuffledOptions = this.shuffleArray([0, 1, 2, 3]);
      while (count < 2) {
        const option: HymnWithStatus = this.options[shuffledOptions[index++]];
        if (option.hymnNumber != this.winningOption?.hymnNumber) {
          option.status = "invisible";
          count++;
        }
      }
    }
  }

  onHintLyrics() {
    if(!this.hintUsedLyrics) {
      this.hintUsedLyrics = true;
      this.lyricsHintsLeft--;

      const randomVerseIndex = Math.floor(Math.random() * this.winningOption!.numberOfVerses);
      const randomVerse = this.winningOption!.verses[randomVerseIndex].content;

      if(randomVerse.length > 2) {
        const randomLineIndex = Math.min(
          Math.max(Math.floor(Math.random() * (randomVerse.length - 1)), 1),
          randomVerse.length - 2
        );
        const lyrics = "♫  " + randomVerse[randomLineIndex] + " \n " + randomVerse[randomLineIndex+1] + "  ♪";
        this.subMessage = lyrics;
      } else {
        let lyrics = "";
      }
    }
  }

  shuffleArray(array: any[]): any[] {
    for (let i = array.length - 1; i > 0; i--) {
      // Generate a random index from 0 to i
      const j = Math.floor(Math.random() * (i + 1));
      // Swap elements at indices i and j
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }

  getRoundDuration() {
    return this.timeRoundEnd - this.timeRoundStart;
  }

  awardMessage: string | null = null;
  @ViewChild('pointsAward') pointsAwardRef!: ElementRef<HTMLElement>;
  showPointsAward(message: string) {
    this.awardMessage = message;
    const pointsAwardElement = this.pointsAwardRef?.nativeElement;

    if (pointsAwardElement) {
      pointsAwardElement.classList.remove('animate');

      // By reading the offsetWidth, we force the browser to process the style changes above.
      // The 'void' operator is used to prevent any potential linting errors about unused expressions.
      void pointsAwardElement.offsetWidth;

      // Now, add the class back in the same execution frame. The browser will see this
      // as a new state change and restart the animation.
      pointsAwardElement.classList.add('animate');

      pointsAwardElement.addEventListener('animationend', () => {
        this.awardMessage = null;
        // No need to remove 'animate' here if it's an out-animation,
        // but it's good practice for resetting.
        pointsAwardElement.classList.remove('animate');
      }, { once: true });
    }
  }

  playMidi() {
    this.midiplay.emit();
  }

  stopMidi() {
    this.midistop.emit();
  }

  protected readonly translation = translation;
}
